import moment, {Moment} from 'moment';
import React, {FC, useRef, useState} from 'react';
import Divider from '@material-ui/core/Divider';
import {getLocale} from 'epic-common/src/locale';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import DatePicker from './DatePicker';
import ArrowSwitcher from './views/ArrowSwitcher';
import MonthCalendar from './views/month/MonthCalendar';

import {DateMoment} from '../../../types';
import SlideTransition from './views/SlideTransition';

import {useStyles} from './MonthRangePicker.styles';

export enum Views {
    Month = 'month',
    Year = 'year'
}

export interface MonthRangePicker {
    startDate: DateMoment;
    endDate: DateMoment;
    isPreselection?: boolean;
    max?: DateMoment;
    min?: DateMoment;
    view?: Views;
    locale?: string;
    year?: number;
    onChange: (date: Moment) => void;
}

const MonthRangePicker: FC<MonthRangePicker> = ({
    locale = getLocale(),
    startDate: startDateProps,
    endDate: endDateProps,
    isPreselection,
    max = new Date(),
    min,
    year,
    view: viewProps = Views.Month,
    onChange
}: MonthRangePicker) => {
    const startDate = moment(startDateProps);
    const endDate = moment(endDateProps);

    const [view, setView] = useState<Views>(viewProps);
    const [currentYear, setCurrentYear] = useState<number>(year || endDate.year());

    const classes = useStyles();
    const direction = useRef(null);

    const isLeftDisabled =
        min && moment(new Date(currentYear - 1, 1, 1, 0, 0, 0)).isBefore(min, 'year');
    const isRightDisabled = moment(new Date(currentYear + 1, 1, 1, 0, 0, 0)).isAfter(max, 'year');

    const selectPreviousYear = () => {
        setCurrentYear(current => current - 1);
        direction.current = 'right';
    };

    const selectNextYear = () => {
        setCurrentYear(current => current + 1);
        direction.current = 'left';
    };

    const handleYearChange = (date: Moment) => {
        setCurrentYear(date.year());
        setView(Views.Month);
    };

    const toggleView = () => {
        setView(currentView => (currentView === Views.Month ? Views.Year : Views.Month));
    };

    const ArrowIcon = view === Views.Month ? ArrowDropDownIcon : ArrowDropUpIcon;

    return (
        <div className={classes.pickerView}>
            <div className={classes.switchHeader}>
                <ArrowSwitcher
                    isLeftDisabled={isLeftDisabled}
                    isRightDisabled={isRightDisabled}
                    isRightHidden={view === Views.Year}
                    isLeftHidden={view === Views.Year}
                    onLeftClick={selectPreviousYear}
                    onRightClick={selectNextYear}>
                    <SlideTransition
                        transKey={currentYear}
                        reduceAnimations={view === Views.Year}
                        slideDirection={direction.current}
                        className={classes.switchHeaderTransition}>
                        <div className={classes.yearSwitcher}>
                            <Typography aria-live="polite" align="center" variant="button">
                                {currentYear}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={toggleView}
                                className={classes.yearSelectionSwitcher}>
                                <ArrowIcon className={classes.switchViewDropdown} />
                            </IconButton>
                        </div>
                    </SlideTransition>
                </ArrowSwitcher>
            </div>
            {/* Year Picker View */}
            {view === Views.Year && (
                <>
                    <Divider />
                    <DatePicker
                        autoOk
                        disableFuture
                        disableToolbar
                        variant="static"
                        openTo="year"
                        value={new Date(currentYear, 1, 1, 0, 0, 0)}
                        onChange={handleYearChange}
                        views={['year']}
                    />
                </>
            )}
            {/* Year Picker View */}

            {/* Month Picker View */}
            {view === Views.Month && (
                <SlideTransition transKey={currentYear} slideDirection={direction.current}>
                    <MonthCalendar
                        startDate={startDate}
                        endDate={endDate}
                        currentYear={currentYear}
                        min={min}
                        max={max}
                        locale={locale}
                        isPreselection={isPreselection}
                        onChange={onChange}
                    />
                </SlideTransition>
            )}
            {/* Month Picker View */}
        </div>
    );
};

export default MonthRangePicker;
